/**
 * Admin: Environment Editor Page
 * Create or edit a lab environment definition.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { apiGet, apiPost, apiPut, ApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import type { LabEnvironmentDefinition, InstanceDefinition } from '@cloak-next/shared';

const LINUX_STARTER = '#!/bin/bash\nset -e\n\n# Instance ID: {{instanceId}}\n# Domain:      {{rootDomain}}\n\n# Your setup commands here\n';
const WINDOWS_STARTER = '<powershell>\n# Instance ID: {{instanceId}}\n# Domain:      {{rootDomain}}\n\n# Your setup commands here\n</powershell>\n';

const INSTANCE_TYPES = [
  { value: 't3.micro', label: 't3.micro (2 vCPU, 1 GB)' },
  { value: 't3.small', label: 't3.small (2 vCPU, 2 GB)' },
  { value: 't3.medium', label: 't3.medium (2 vCPU, 4 GB)' },
  { value: 't3.large', label: 't3.large (2 vCPU, 8 GB)' },
  { value: 't3.xlarge', label: 't3.xlarge (4 vCPU, 16 GB)' },
  { value: 't3.2xlarge', label: 't3.2xlarge (8 vCPU, 32 GB)' },
];

function emptyInstance(): InstanceDefinition {
  return {
    id: '',
    instanceType: 't3.medium',
    volumeSizeGB: 30,
    isPublic: true,
    dns: 'wildcard',
    ports: [
      { protocol: 'tcp', port: 443 },
      { protocol: 'tcp', port: 80 },
    ],
    userDataScript: '#!/bin/bash\nset -e\n',
  };
}

export function EnvironmentEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  // Basic info
  const [envId, setEnvId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [estimatedCostPerHour, setEstimatedCostPerHour] = useState('0.04');
  const [category, setCategory] = useState<string>('web-apps');
  const [dashboardUrl, setDashboardUrl] = useState('');

  // Instances
  const [instances, setInstances] = useState<InstanceDefinition[]>([emptyInstance()]);

  // Getting-started template
  const [gettingStartedTemplate, setGettingStartedTemplate] = useState('');

  // UI state
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEnvironment = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await apiGet<{ success: boolean; data: LabEnvironmentDefinition & { updatedAt?: string } }>(
        `/admin/environments/${encodeURIComponent(id)}`
      );
      const env = response.data;
      setEnvId(env.id);
      setName(env.name);
      setDescription(env.description || '');
      setEstimatedCostPerHour(String(env.estimatedCostPerHour));
      setCategory(env.category || 'web-apps');
      setDashboardUrl(env.dashboardUrl || '');
      setInstances(env.instances.map(inst => ({
        ...inst,
        isPublic: inst.isPublic !== false,
        dns: inst.dns ?? ((inst as any).wildcardDns === true ? 'wildcard' : undefined),
      })));
      setGettingStartedTemplate(env.gettingStartedTemplate || '');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load environment');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) loadEnvironment();
  }, [isEdit, loadEnvironment]);

  // ─── Instance editing helpers ─────────────────────────────────────────────

  const updateInstance = (index: number, patch: Partial<InstanceDefinition>) => {
    setInstances((prev) => prev.map((inst, i) => (i === index ? { ...inst, ...patch } : inst)));
  };

  const addInstance = () => {
    setInstances((prev) => [...prev, emptyInstance()]);
  };

  const removeInstance = (index: number) => {
    setInstances((prev) => prev.filter((_, i) => i !== index));
  };

  const addPort = (instIndex: number) => {
    updateInstance(instIndex, {
      ports: [...instances[instIndex].ports, { protocol: 'tcp', port: 80 }],
    });
  };

  const updatePort = (
    instIndex: number,
    portIndex: number,
    patch: { protocol?: 'tcp' | 'udp'; port?: number }
  ) => {
    const updated = instances[instIndex].ports.map((p, i) =>
      i === portIndex ? { ...p, ...patch } : p
    );
    updateInstance(instIndex, { ports: updated });
  };

  const removePort = (instIndex: number, portIndex: number) => {
    updateInstance(instIndex, {
      ports: instances[instIndex].ports.filter((_, i) => i !== portIndex),
    });
  };

  // ─── Save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setError(null);

    // Validate
    if (!envId.trim()) {
      setError('Environment ID is required');
      return;
    }
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (instances.length === 0) {
      setError('At least one host is required');
      return;
    }
    for (const inst of instances) {
      if (!inst.id.trim()) {
        setError('All hosts must have an ID');
        return;
      }

    }

    const payload: LabEnvironmentDefinition = {
      id: envId.trim(),
      name: name.trim(),
      description: description.trim(),
      category: (category as any) || undefined,
      instances,
      dashboardUrl: dashboardUrl.trim() || undefined,
      gettingStartedTemplate,
      estimatedCostPerHour: parseFloat(estimatedCostPerHour) || 0,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await apiPut(`/admin/environments/${encodeURIComponent(id!)}`, payload);
      } else {
        await apiPost('/admin/environments', payload);
      }
      navigate('/admin/environments');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="text-center py-12 text-muted-foreground">Loading environment...</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">{isEdit ? 'Edit Environment' : 'New Environment'}</h2>
          {isEdit && (
            <p className="text-muted-foreground mt-1 font-mono text-sm">{id}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/environments')} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Environment'}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* ── Section 1: Basic Info ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Basic Information</h3>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* ID — read-only in edit mode */}
          <div className="space-y-1">
            <Label htmlFor="env-id">Environment ID</Label>
            <Input
              id="env-id"
              value={envId}
              onChange={(e) => setEnvId(e.target.value)}
              disabled={isEdit}
              placeholder="e.g. web-apps-katana"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Unique slug used in DynamoDB and class creation. Cannot be changed after creation.
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="env-name">Display Name</Label>
            <Input
              id="env-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Web Applications with Katana"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="env-desc">Description</Label>
            <Input
              id="env-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description shown in the class creation picker"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="env-dashboard-url">Dashboard URL (optional)</Label>
            <Input
              id="env-dashboard-url"
              value={dashboardUrl}
              onChange={(e) => setDashboardUrl(e.target.value)}
              placeholder="e.g. https://katana.{{rootDomain}}"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Handlebars template for the student's lab button URL. Leave blank to hide the button.
              Available variables: <code className="font-mono">{'{{rootDomain}}'}</code> (e.g. <code className="font-mono">a7k9m2.penlabs.net</code>).
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="env-cost">Estimated Cost / hr ($)</Label>
              <Input
                id="env-cost"
                type="number"
                min="0"
                step="0.0001"
                value={estimatedCostPerHour}
                onChange={(e) => setEstimatedCostPerHour(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="env-category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="env-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="web-apps">Web Apps</SelectItem>
                  <SelectItem value="network">Network</SelectItem>
                  <SelectItem value="api">API</SelectItem>
                  <SelectItem value="mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Section 2: Hosts ──────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Hosts</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Each host is a separate EC2 instance. Most environments use a single host.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={addInstance}
              disabled={instances.length >= 5}
            >
              Add Host
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {instances.map((inst, instIdx) => (
            <div key={instIdx} className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">
                  Host {instIdx + 1}{inst.id ? `: ${inst.id}` : ''}
                </h4>
                {instances.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeInstance(instIdx)}
                  >
                    Remove
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Host ID (slug)</Label>
                  <Input
                    value={inst.id}
                    onChange={(e) => updateInstance(instIdx, { id: e.target.value })}
                    placeholder="e.g. katana"
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    Used in templates as <code className="font-mono">{'{{instances.<id>.hostname}}'}</code>, <code className="font-mono">{'{{instances.<id>.privateIp}}'}</code>
                  </p>
                </div>
                <div className="space-y-1">
                  <Label>Instance Type</Label>
                  <Select
                    value={inst.instanceType}
                    onValueChange={(v) => updateInstance(instIdx, { instanceType: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INSTANCE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Volume Size (GB)</Label>
                  <Input
                    type="number"
                    min="8"
                    max="1000"
                    value={inst.volumeSizeGB}
                    onChange={(e) => updateInstance(instIdx, { volumeSizeGB: parseInt(e.target.value) || 30 })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>AMI Override (optional)</Label>
                  <Input
                    value={inst.amiOverride || ''}
                    onChange={(e) => updateInstance(instIdx, { amiOverride: e.target.value || undefined })}
                    placeholder="Default: Ubuntu 22.04 LTS"
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`public-${instIdx}`}
                    checked={inst.isPublic !== false}
                    onCheckedChange={(checked) => updateInstance(instIdx, { isPublic: Boolean(checked) })}
                  />
                  <Label htmlFor={`public-${instIdx}`} className="cursor-pointer">
                    Public host (public subnet, gets a public IP and optional DNS)
                  </Label>
                </div>
                {inst.isPublic !== false ? (
                  <div className="flex items-center gap-3 pl-6">
                    <Label className="shrink-0">DNS record</Label>
                    <Select
                      value={inst.dns ?? 'none'}
                      onValueChange={(v) => updateInstance(instIdx, { dns: v === 'none' ? undefined : v as InstanceDefinition['dns'] })}
                    >
                      <SelectTrigger className="w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="wildcard">Wildcard (*.instanceId.domain)</SelectItem>
                        <SelectItem value="explicit">Explicit (instanceId.domain)</SelectItem>
                        <SelectItem value="both">Both wildcard + explicit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <p className="pl-6 text-xs text-muted-foreground">
                    Private host — no public IP or DNS. Use{' '}
                    <code className="font-mono">{'{{instances.' + (inst.id || 'hostId') + '.privateIp}}'}</code>{' '}
                    in the getting-started template to show the private IP to students.
                  </p>
                )}
              </div>

              {/* Ports */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Open Ports</Label>
                  <Button variant="outline" size="sm" onClick={() => addPort(instIdx)}>
                    Add Port
                  </Button>
                </div>
                {inst.ports.map((port, portIdx) => (
                  <div key={portIdx} className="flex gap-2 items-center">
                    <Select
                      value={port.protocol}
                      onValueChange={(v) => updatePort(instIdx, portIdx, { protocol: v as 'tcp' | 'udp' })}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tcp">TCP</SelectItem>
                        <SelectItem value="udp">UDP</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="1"
                      max="65535"
                      value={port.port}
                      onChange={(e) => updatePort(instIdx, portIdx, { port: parseInt(e.target.value) || 443 })}
                      className="w-28"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={() => removePort(instIdx, portIdx)}
                      disabled={inst.isPublic !== false && inst.ports.length <= 1}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>

              {/* Health Check */}
              <div className="space-y-2">
                <Label>Health Check (optional)</Label>
                <div className="flex gap-2 items-start">
                  <div className="flex items-center gap-2 mt-2">
                    <Checkbox
                      id={`hc-enabled-${instIdx}`}
                      checked={Boolean(inst.healthCheck)}
                      onCheckedChange={(checked) =>
                        updateInstance(instIdx, {
                          healthCheck: checked
                            ? { type: 'tcp', port: 443, timeoutSeconds: 300 }
                            : undefined,
                        })
                      }
                    />
                    <Label htmlFor={`hc-enabled-${instIdx}`} className="cursor-pointer">
                      Enable
                    </Label>
                  </div>
                  {inst.healthCheck && (
                    <div className="flex gap-2 flex-wrap">
                      <Select
                        value={inst.healthCheck.type}
                        onValueChange={(v) =>
                          updateInstance(instIdx, {
                            healthCheck: { ...inst.healthCheck!, type: v as 'http' | 'tcp' },
                          })
                        }
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tcp">TCP</SelectItem>
                          <SelectItem value="http">HTTP</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="1"
                        max="65535"
                        value={inst.healthCheck.port}
                        onChange={(e) =>
                          updateInstance(instIdx, {
                            healthCheck: {
                              ...inst.healthCheck!,
                              port: parseInt(e.target.value) || 443,
                            },
                          })
                        }
                        className="w-28"
                        placeholder="Port"
                      />
                      <Input
                        type="number"
                        min="10"
                        max="600"
                        value={inst.healthCheck.timeoutSeconds || 300}
                        onChange={(e) =>
                          updateInstance(instIdx, {
                            healthCheck: {
                              ...inst.healthCheck!,
                              timeoutSeconds: parseInt(e.target.value) || 300,
                            },
                          })
                        }
                        className="w-28"
                        placeholder="Timeout (s)"
                      />
                      {inst.healthCheck.type === 'http' && (
                        <Input
                          value={inst.healthCheck.path || '/'}
                          onChange={(e) =>
                            updateInstance(instIdx, {
                              healthCheck: { ...inst.healthCheck!, path: e.target.value },
                            })
                          }
                          className="w-40"
                          placeholder="Path (e.g. /)"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* UserData Script */}
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <Label>UserData Script</Label>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateInstance(instIdx, { userDataScript: LINUX_STARTER })}
                    >
                      Linux Starter
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateInstance(instIdx, { userDataScript: WINDOWS_STARTER })}
                    >
                      Windows Starter
                    </Button>
                  </div>
                </div>
                <textarea
                  className="w-full min-h-[300px] font-mono text-xs border rounded p-3 resize-y"
                  value={inst.userDataScript}
                  onChange={(e) => updateInstance(instIdx, { userDataScript: e.target.value })}
                  placeholder="#!/bin/bash&#10;set -e&#10;..."
                  spellCheck={false}
                />
                <p className="text-xs text-muted-foreground">
                  Script run on instance launch. Use <code className="font-mono">{'#!/bin/bash'}</code> for Linux or{' '}
                  <code className="font-mono">{'<powershell>...</powershell>'}</code> for Windows.{' '}
                  Handlebars variables:{' '}
                  <code className="font-mono">{'{{instanceId}}'}</code> (e.g. <code className="font-mono">a7k9m2</code>),{' '}
                  <code className="font-mono">{'{{rootDomain}}'}</code> (e.g. <code className="font-mono">a7k9m2.penlabs.net</code>),{' '}
                  <code className="font-mono">{'{{domain}}'}</code> (e.g. <code className="font-mono">penlabs.net</code>),{' '}
                  <code className="font-mono">{'{{region}}'}</code> (e.g. <code className="font-mono">us-east-1</code>).
                </p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ── Section 3: Getting-Started Template ───────────────────────────── */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Getting-Started Template</h3>
          <p className="text-sm text-muted-foreground">
            Markdown shown to students when they view their lab. Handlebars variables:{' '}
            <code className="font-mono text-xs">{'{{className}}'}</code>,{' '}
            <code className="font-mono text-xs">{'{{instanceDomain}}'}</code> (e.g. <code className="font-mono text-xs">a7k9m2.penlabs.net</code>),{' '}
            <code className="font-mono text-xs">{'{{instances.hostId.hostname}}'}</code> (public hosts with DNS),{' '}
            <code className="font-mono text-xs">{'{{instances.hostId.publicIp}}'}</code> (public hosts),{' '}
            <code className="font-mono text-xs">{'{{instances.hostId.privateIp}}'}</code> (all hosts).
          </p>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="write">
            <TabsList>
              <TabsTrigger value="write">Write</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>
            <TabsContent value="write">
              <textarea
                className="w-full min-h-[300px] font-mono text-sm border rounded p-3 resize-y"
                value={gettingStartedTemplate}
                onChange={(e) => setGettingStartedTemplate(e.target.value)}
                placeholder="# Welcome to {{className}}&#10;&#10;## Your Lab Environment..."
                spellCheck={false}
              />
            </TabsContent>
            <TabsContent value="preview">
              {gettingStartedTemplate ? (
                <div className="prose prose-sm max-w-none border rounded p-4">
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{gettingStartedTemplate}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic p-4">
                  Nothing to preview yet. Write some Markdown in the Write tab.
                </p>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Bottom Save / Cancel */}
      <div className="flex gap-2 justify-end pb-8">
        <Button variant="outline" onClick={() => navigate('/admin/environments')} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Environment'}
        </Button>
      </div>
    </div>
  );
}
